import React, { useState, useRef } from 'react';
import { Modal, Form, Row, Col, Badge, Button, Spinner, Alert, ListGroup } from 'react-bootstrap';
import { BsEye, BsPlusCircle, BsTrash, BsPencil, BsExclamationTriangle } from 'react-icons/bs';
import { BiCheck, BiPencil, BiX } from 'react-icons/bi';
import { toast } from 'react-toastify';
import api from '../../../api';
import BackendErrorMessage from '../../../components/BackendErrorMessage';

export interface Unidade {
    id: string;
    nome: string;
    clientId: string;
}

export interface Link {
    rel: string;
    href: string;
}

export interface Cronograma {
    id: string;
    atividades: any[]; // Você pode tipar melhor se souber a estrutura
    _links: Link[];
}

export interface Edital {
    id: string;
    nome: string;
    processoId: string;
    processoNome: string;
    candidatos: any[]; // Você pode tipar melhor se souber a estrutura
}

export interface Processo {
    id: string;
    nome: string;
    unidade: Unidade;
    identificador: string;
    responsavel: string;
    cronograma: Cronograma;
    salaEspera: any | null; // Você pode tipar melhor
    dataCriacao: string;
    status: string;
    _links: Link[];
    editais: Edital[];
    numeroCandidatos: number;
    finalizavel: boolean;
    convocacoesEnviadas: boolean;
    nomeEditavel: boolean;
    cronogramaDefinido: boolean;
    bancasTerminadas: boolean;
}

interface CandidatoEdital {
    id: string;
    inscricao: string;
    candidatoId: string;
    editalId: string;
    nomeEdital: string;
}

interface CandidatoResponse {
    candidatoId?: string;
    id?: string;
    nome: string;
    nomeSocial: string | null;
    candidatoEditais: CandidatoEdital[];
    cpf: string | null;
    passaporte: string | null;
    email: string | null;
    status: string;
    autodeclaracaoRacial?: string | null;
    candidatoResponsavel?: {
        nome: string;
        nomeSocial: string;
        cpf: string;
        passaporte: string;
        email: string;
    };
}

interface NovoEdital {
    inscricao: string;
    editalId: string;
    nomeEdital: string;
}

interface EditarCandidatoModalProps {
    show: boolean;
    onHide: () => void;
    candidato: CandidatoResponse | null;
    editMode: boolean;
    processoId: string;
    onSaveSuccess: () => void;
}

const EditarCandidatoModal: React.FC<EditarCandidatoModalProps> = ({
    show,
    onHide,
    candidato,
    editMode,
    processoId,
    onSaveSuccess,
    
}) => {
    const [currentCandidato, setCurrentCandidato] = useState<CandidatoResponse | null>(candidato);
    const [saving, setSaving] = useState(false);
    const [novoEdital, setNovoEdital] = useState<NovoEdital>({
        inscricao: '',
        editalId: '',
        nomeEdital: ''
    });
    const [editandoInscricaoId, setEditandoInscricaoId] = useState<string | null>(null);
    const [inscricaoEditada, setInscricaoEditada] = useState('');
    const [editaisDisponiveis, setEditaisDisponiveis] = useState<{ id: string; nome: string }[]>([]);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const fetchProcesso = async (processoId: string) => {        

        try {
            const response = await api.get(`/processos/${processoId}`);

            const processoData: Processo = response.data;

            setEditaisDisponiveis(processoData.editais || []);

            return processoData;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
            toast.error(BackendErrorMessage(err));
            throw err;
        } finally {
        }
    };

    React.useEffect(() => {
        if (candidato) {
            console.log('Candidato recebido no modal:', candidato);
            console.log('Responsável do candidato:', candidato.candidatoResponsavel);
        }
        setCurrentCandidato(candidato);
        setEditandoInscricaoId(null);
        setInscricaoEditada('');
        setNovoEdital({
            inscricao: '',
            editalId: '',
            nomeEdital: ''
        });
        setConfirmingDelete(false);
        if (deleteTimeoutRef.current) {
            clearTimeout(deleteTimeoutRef.current);
            deleteTimeoutRef.current = null;
        }
        fetchProcesso(processoId);
    }, [candidato]);

    const handleChange = (e: any) => {
        if (!currentCandidato) return;
        const { name, value } = e.target;

        if (name.startsWith('responsavel.')) {
            const key = name.split('.')[1];
            setCurrentCandidato({
                ...currentCandidato,
                candidatoResponsavel: {
                    ...(currentCandidato.candidatoResponsavel || {
                        nome: '', email: '', nomeSocial: '', cpf: '', passaporte: ''
                    }),
                    [key]: value
                }
            });
        } else {
            setCurrentCandidato({ ...currentCandidato, [name]: value });
        }
    };

    const handleAddResponsavel = () => {
        if (!currentCandidato) return;
        setCurrentCandidato({
            ...currentCandidato,
            candidatoResponsavel: {
                nome: '',
                email: '',
                nomeSocial: '',
                cpf: '',
                passaporte: ''
            }
        });
    };

    const handleRemoveResponsavel = () => {
        if (!currentCandidato) return;
        setCurrentCandidato({
            ...currentCandidato,
            candidatoResponsavel: undefined
        });
    };

    const handleNovoEditalChange = (e: any) => {
        const { name, value } = e.target;
        
        if (name === 'editalId') {
            const editalSelecionado = editaisDisponiveis.find(edital => edital.id === value);
            setNovoEdital({
                ...novoEdital,
                [name]: value,
                nomeEdital: editalSelecionado ? editalSelecionado.nome : ''
            });
        } else {
            setNovoEdital({
                ...novoEdital,
                [name]: value
            });
        }
    };

    const handleAdicionarInscricao = () => {
        if (!currentCandidato || !novoEdital.editalId || !novoEdital.inscricao) {
            toast.error('Preencha todos os campos para adicionar uma inscrição');
            return;
        }

        const novaInscricao: CandidatoEdital = {
            id: `temp-${Date.now()}`,
            inscricao: novoEdital.inscricao,
            candidatoId: currentCandidato.candidatoId || currentCandidato.id || '',
            editalId: novoEdital.editalId,
            nomeEdital: novoEdital.nomeEdital
        };

        setCurrentCandidato({
            ...currentCandidato,
            candidatoEditais: [...currentCandidato.candidatoEditais, novaInscricao]
        });

        setNovoEdital({
            inscricao: '',
            editalId: '',
            nomeEdital: ''
        });
    };

    const handleRemoverInscricao = (id: string) => {
        if (!currentCandidato) return;
        
        setCurrentCandidato({
            ...currentCandidato,
            candidatoEditais: currentCandidato.candidatoEditais.filter(edital => edital.id !== id)
        });
    };

    const handleIniciarEdicaoInscricao = (id: string, inscricao: string) => {
        setEditandoInscricaoId(id);
        setInscricaoEditada(inscricao);
    };

    const handleSalvarEdicaoInscricao = () => {
        if (!currentCandidato || !editandoInscricaoId) return;
        
        setCurrentCandidato({
            ...currentCandidato,
            candidatoEditais: currentCandidato.candidatoEditais.map(edital => 
                edital.id === editandoInscricaoId 
                    ? { ...edital, inscricao: inscricaoEditada }
                    : edital
            )
        });
        
        setEditandoInscricaoId(null);
        setInscricaoEditada('');
    };

    const handleCancelarEdicaoInscricao = () => {
        setEditandoInscricaoId(null);
        setInscricaoEditada('');
    };

    const handleDelete = async () => {
        if (!currentCandidato) return;

        if (!confirmingDelete) {
            setConfirmingDelete(true);
            deleteTimeoutRef.current = setTimeout(() => {
                setConfirmingDelete(false);
            }, 5000);
        } else {
            if (deleteTimeoutRef.current) {
                clearTimeout(deleteTimeoutRef.current);
            }
            try {
                setDeleteLoading(true);
                
                const candidatoId = currentCandidato.candidatoId || currentCandidato.id;
                if (!candidatoId) {
                    toast.error('ID do candidato não encontrado');
                    return;
                }

                const candidatoEditaisRequest = currentCandidato.candidatoEditais.map(edital => ({
                    id: edital.id.startsWith('temp-') ? undefined : edital.id,
                    inscricao: edital.inscricao,
                    candidatoId: edital.candidatoId,
                    editalId: edital.editalId,
                    nomeEdital: edital.nomeEdital
                }));

                const requestBody = {
                    nome: currentCandidato.nome,
                    candidatoEditais: candidatoEditaisRequest,
                    nomeSocial: currentCandidato.nomeSocial,
                    cpf: currentCandidato.cpf,
                    passaporte: currentCandidato.passaporte,
                    email: currentCandidato.email,
                    status: 'DELETADO',
                    candidatoResponsavel: currentCandidato.candidatoResponsavel
                };

                await api.put(`/processos/${processoId}/candidatos/${candidatoId}`, requestBody);
                toast.success('Candidato deletado com sucesso!');
                onSaveSuccess();
                onHide();
            } catch (error) {
                toast.error(BackendErrorMessage(error));
            } finally {
                setDeleteLoading(false);
                setConfirmingDelete(false);
            }
        }
    };

    const handleSave = async () => {
        if (!currentCandidato) {
            toast.error('Nenhum candidato selecionado');
            return;
        }

        if (!processoId) {
            toast.error('ID do processo não encontrado');
            return;
        }

        const candidatoId = currentCandidato.candidatoId || currentCandidato.id;
        if (!candidatoId) {
            toast.error('ID do candidato não encontrado');
            return;
        }

        // Validar se há pelo menos uma inscrição
        if (currentCandidato.candidatoEditais.length === 0) {
            toast.error('O candidato deve ter pelo menos uma inscrição');
            return;
        }

        const candidatoEditaisRequest = currentCandidato.candidatoEditais.map(edital => ({
            id: edital.id.startsWith('temp-') ? undefined : edital.id,
            inscricao: edital.inscricao,
            candidatoId: edital.candidatoId,
            editalId: edital.editalId,
            nomeEdital: edital.nomeEdital
        }));

        const requestBody = {
            nome: currentCandidato.nome,
            candidatoEditais: candidatoEditaisRequest,
            nomeSocial: currentCandidato.nomeSocial || '',
            cpf: currentCandidato.cpf,
            passaporte: currentCandidato.passaporte || '',
            email: currentCandidato.email,
            autodeclaracaoRacial: currentCandidato.autodeclaracaoRacial || null,
            status: currentCandidato.status,
            responsavel: currentCandidato.candidatoResponsavel ? {
                nome: currentCandidato.candidatoResponsavel.nome,
                nomeSocial: currentCandidato.candidatoResponsavel.nomeSocial || '',
                cpf: currentCandidato.candidatoResponsavel.cpf || '',
                passaporte: currentCandidato.candidatoResponsavel.passaporte || '',
                email: currentCandidato.candidatoResponsavel.email
            } : null
        };

        try {
            setSaving(true);
            await api.put(`/processos/${processoId}/candidatos/${candidatoId}`, requestBody);
            toast.success('Candidato atualizado com sucesso!');
            onHide();
            onSaveSuccess();
        } catch (error: any) {
            console.error('Erro ao salvar candidato:', error);
            const errorMsg = BackendErrorMessage(error) || 'Dados do Candidato ou Responsável inválidos ou não preenchidos.';
            toast.error(errorMsg);
        } finally {
            setSaving(false);
        }
    };

    if (!currentCandidato) return null;

    return (
        <Modal show={show} onHide={onHide} size="lg" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>
                    {editMode ? <BiPencil className="me-2" /> : <BsEye className="me-2" />}
                    {editMode ? 'Editar' : 'Visualizar'} Candidato
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <Form>
                    {/* Seção de Editais e Inscrições */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">Editais e Inscrições</h5>
                        <Badge bg="secondary">{currentCandidato.candidatoEditais.length} editais</Badge>
                    </div>

                    {editMode && (
                        <div className="border p-3 mb-3 rounded bg-light">
                            <h6 className="mb-3">Adicionar Nova Inscrição</h6>
                            <Row className="g-2 align-items-end">
                                <Col md={5}>
                                    <Form.Group className="mb-2">
                                        <Form.Label className="small mb-1">Edital</Form.Label>
                                        <Form.Select
                                            name="editalId"
                                            value={novoEdital.editalId}
                                            onChange={handleNovoEditalChange}
                                            size="sm"
                                        >
                                            <option value="">Selecione um edital</option>
                                            {editaisDisponiveis.map(edital => (
                                                <option key={edital.id} value={edital.id}>
                                                    {edital.nome}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={5}>
                                    <Form.Group className="mb-2">
                                        <Form.Label className="small mb-1">Número de Inscrição</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="inscricao"
                                            value={novoEdital.inscricao}
                                            onChange={handleNovoEditalChange}
                                            size="sm"
                                            placeholder="Número da inscrição"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={2}>
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={handleAdicionarInscricao}
                                        className="w-100"
                                    >
                                        <BsPlusCircle className="me-1" />
                                        Adicionar
                                    </Button>
                                </Col>
                            </Row>
                        </div>
                    )}

                    {currentCandidato.candidatoEditais.length > 0 ? (
                        <ListGroup variant="flush" className="mb-3">
                            {currentCandidato.candidatoEditais.map((edital) => (
                                <ListGroup.Item key={edital.id} className="px-0 py-2">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div className="flex-grow-1">
                                            <div className="fw-bold">{edital.nomeEdital}</div>
                                            {editandoInscricaoId === edital.id ? (
                                                <div className="d-flex align-items-center mt-1">
                                                    <Form.Control
                                                        type="text"
                                                        value={inscricaoEditada}
                                                        onChange={(e) => setInscricaoEditada(e.target.value)}
                                                        size="sm"
                                                        className="me-2"
                                                        style={{ width: '150px' }}
                                                    />
                                                    <Button
                                                        variant="success"
                                                        size="sm"
                                                        onClick={handleSalvarEdicaoInscricao}
                                                        className="me-1"
                                                    >
                                                        <BiCheck />
                                                    </Button>
                                                    <Button
                                                        variant="secondary"
                                                        size="sm"
                                                        onClick={handleCancelarEdicaoInscricao}
                                                    >
                                                        <BiX />
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div className="d-flex align-items-center mt-1">
                                                    <span className="text-muted">Inscrição: {edital.inscricao}</span>
                                                    {editMode && (
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            className="ms-2"
                                                            onClick={() => handleIniciarEdicaoInscricao(edital.id, edital.inscricao)}
                                                        >
                                                            <BsPencil />
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        {editMode && (
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleRemoverInscricao(edital.id)}
                                            >
                                                <BsTrash />
                                            </Button>
                                        )}
                                    </div>
                                </ListGroup.Item>
                            ))}
                        </ListGroup>
                    ) : (
                        <Alert variant="info" className="py-2">
                            Nenhuma inscrição cadastrada para este candidato.
                        </Alert>
                    )}

                    <hr className="my-3" />

                    <h5 className="mb-3">Dados do Candidato</h5>

                    <Row className="g-2">
                        <Col md={8}>
                            <Form.Group className="mb-2">
                                <Form.Label className="small">Nome Completo *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="nome"
                                    value={currentCandidato.nome}
                                    onChange={handleChange}
                                    disabled={!editMode}
                                    size="sm"
                                    required
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-2">
                                <Form.Label className="small">Nome Social</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="nomeSocial"
                                    value={currentCandidato.nomeSocial || ''}
                                    onChange={handleChange}
                                    disabled={!editMode}
                                    size="sm"
                                    placeholder="Opcional"
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row className="g-2">
                        <Col md={6}>
                            <Form.Group className="mb-2">
                                <Form.Label className="small">CPF</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="cpf"
                                    value={currentCandidato.cpf || ''}
                                    onChange={handleChange}
                                    disabled={!editMode}
                                    size="sm"
                                    placeholder="123.456.789-09"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-2">
                                <Form.Label className="small">Passaporte</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="passaporte"
                                    value={currentCandidato.passaporte || ''}
                                    onChange={handleChange}
                                    disabled={!editMode}
                                    size="sm"
                                    placeholder="AB123456"
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row className="g-2">
                        <Col md={8}>
                            <Form.Group className="mb-2">
                                <Form.Label className="small">E-mail</Form.Label>
                                <Form.Control
                                    type="email"
                                    name="email"
                                    value={currentCandidato.email || ''}
                                    onChange={handleChange}
                                    disabled={!editMode}
                                    size="sm"
                                    placeholder="fulano@email.com"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-2">
                                <Form.Label className="small">Status</Form.Label>
                                <Form.Select
                                    name="status"
                                    value={currentCandidato.status}
                                    onChange={handleChange}
                                    disabled={!editMode}
                                    size="sm"
                                >
                                    <option value="NAO_AVALIADO">Não avaliado</option>
                                    <option value="INCONCLUSIVO">Inconclusivo</option>
                                    <option value="AUSENTE">Ausente</option>
                                    <option value="VALIDADO">Validado</option>
                                    <option value="VALIDADO_ANTERIORMENTE">Validado anteriormente</option>
                                    <option value="NAO_VALIDADO">Não Validado</option>
                                    <option value="DELETADO">Deletado</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    <Form.Group className="mb-2">
                        <Form.Label className="small">Autodeclaração Racial</Form.Label>
                        <Form.Select
                            name="autodeclaracaoRacial"
                            value={currentCandidato.autodeclaracaoRacial || ''}
                            onChange={handleChange}
                            disabled={!editMode}
                            size="sm"
                        >
                            <option value="">Não declarado</option>
                            <option value="PRETO">Preto</option>
                            <option value="PARDO">Pardo</option>
                            <option value="INDIGENA">Indígena</option>
                            <option value="BRANCO">Branco</option>
                            <option value="AMARELO">Amarelo</option>
                            <option value="NAO_DECLARADO">Não declarado (explícito)</option>
                        </Form.Select>
                    </Form.Group>

                    {/* Switch para Responsável */}
                    {editMode && (
                        <Form.Group className="mb-2">
                            <Form.Check
                                type="switch"
                                id="has-responsavel"
                                label="Possui responsável legal?"
                                checked={!!currentCandidato.candidatoResponsavel}
                                onChange={() => {
                                    if (currentCandidato.candidatoResponsavel) {
                                        handleRemoveResponsavel();
                                    } else {
                                        handleAddResponsavel();
                                    }
                                }}
                                className="small"
                            />
                        </Form.Group>
                    )}

                    {currentCandidato.candidatoResponsavel && (
                        <>
                            <hr className="my-2" />
                            <h6 className="mb-2">Dados do Responsável Legal</h6>

                            <Row className="g-2">
                                <Col md={8}>
                                    <Form.Group className="mb-2">
                                        <Form.Label className="small">Nome Completo</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="responsavel.nome"
                                            value={currentCandidato.candidatoResponsavel.nome || ''}
                                            onChange={handleChange}
                                            disabled={!editMode}
                                            size="sm"
                                            placeholder="Nome completo do responsável"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-2">
                                        <Form.Label className="small">Nome Social</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="responsavel.nomeSocial"
                                            value={currentCandidato.candidatoResponsavel.nomeSocial || ''}
                                            onChange={handleChange}
                                            disabled={!editMode}
                                            size="sm"
                                            placeholder="Opcional"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Row className="g-2">
                                <Col md={6}>
                                    <Form.Group className="mb-2">
                                        <Form.Label className="small">CPF</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="responsavel.cpf"
                                            value={currentCandidato.candidatoResponsavel.cpf || ''}
                                            onChange={handleChange}
                                            disabled={!editMode}
                                            size="sm"
                                            placeholder="987.654.321-00"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-2">
                                        <Form.Label className="small">Passaporte</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="responsavel.passaporte"
                                            value={currentCandidato.candidatoResponsavel.passaporte || ''}
                                            onChange={handleChange}
                                            disabled={!editMode}
                                            size="sm"
                                            placeholder="XY987654"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mb-2">
                                <Form.Label className="small">E-mail</Form.Label>
                                <Form.Control
                                    type="email"
                                    name="responsavel.email"
                                    value={currentCandidato.candidatoResponsavel.email || ''}
                                    onChange={handleChange}
                                    disabled={!editMode}
                                    size="sm"
                                    placeholder="responsavel@email.com"
                                />
                            </Form.Group>
                        </>
                    )}
                </Form>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-between">
                {editMode && (
                    <Button
                        variant={confirmingDelete ? "danger" : "outline-danger"}
                        size="sm"
                        onClick={handleDelete}
                        disabled={saving || deleteLoading}
                        title={confirmingDelete ? "A exclusão não poderá ser revertida." : "Deletar candidato"}
                    >
                        {deleteLoading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Deletando...
                            </>
                        ) : confirmingDelete ? (
                            <>
                                <BsExclamationTriangle className="me-1" />
                                Confirma exclusão?
                            </>
                        ) : (
                            <>
                                <BsTrash className="me-1" />
                                Deletar Candidato
                            </>
                        )}
                    </Button>
                )}

                <div style={{ display: 'flex', gap: '8px' }}>
                    <Button variant="secondary" size="sm" onClick={onHide} disabled={saving || deleteLoading}>
                        Cancelar
                    </Button>
                    {editMode && (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleSave}
                            disabled={saving || deleteLoading}
                        >
                            {saving ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Salvando...
                                </>
                            ) : (
                                <>
                                    <BiCheck className="me-1" />
                                    Salvar
                                </>
                            )}
                        </Button>
                    )}
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default EditarCandidatoModal;
